import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback
} from "react";

import {
    getProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    subscribeProducts
} from "../services/ProductService";

import ProductVariantService
    from "../services/ProductVariantService";

import ProductShippingService
    from "../services/ProductShippingService";

import { useAuth }
    from "../../contexts/AuthContext";


const BrandProductContext = createContext();


export function BrandProductProvider({

    brandId,

    children

}) {

    const { user } = useAuth();


    /*
    --------------------------------------------------
    Products
    --------------------------------------------------
    */

    const [products, setProducts] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState(null);


    /*
    --------------------------------------------------
    Variants
    --------------------------------------------------
    */

    const [variants, setVariants] = useState([]);


    /*
    --------------------------------------------------
    Shipping
    --------------------------------------------------
    */

    const [shipping, setShipping] = useState(null);


    /*
    --------------------------------------------------
    Selected Product
    --------------------------------------------------
    */

    const [selectedProductId, setSelectedProductId] =
        useState(null);


    /*
    --------------------------------------------------
    Refresh Products
    --------------------------------------------------
    */

    const refreshProducts = useCallback(async () => {

        if (!brandId) {

            setProducts([]);

            setLoading(false);

            return;

        }

        try {

            setLoading(true);

            const list =
                await getProducts(brandId);

            setProducts(list);

            setError(null);

        }

        catch (err) {

            console.error(err);

            setError(err);

        }

        finally {

            setLoading(false);

        }

    }, [brandId]);


    /*
    --------------------------------------------------
    Initial Product Load
    --------------------------------------------------
    */

    useEffect(() => {

        refreshProducts();

    }, [refreshProducts]);


    /*
    --------------------------------------------------
    Real-time Product Subscription
    --------------------------------------------------
    */

    useEffect(() => {

        if (!brandId) return;

        const unsubscribe =
            subscribeProducts(

                brandId,

                () => {

                    refreshProducts();

                }

            );

        return () => {

            if (
                typeof unsubscribe === "function"
            ) {

                unsubscribe();

            }

        };

    }, [
        brandId,
        refreshProducts
    ]);


    /*
    ==================================================
    PRODUCT FUNCTIONS
    ==================================================
    */


    async function addProduct(data) {

        const record =
            await createProduct({

                ...data,

                brand: brandId

            });

        return record;

    }


    async function editProduct(
        id,
        data
    ) {

        return await updateProduct(

            id,

            data

        );

    }


    async function removeProduct(id) {

        return await deleteProduct(id);

    }


    /*
    ==================================================
    VARIANT FUNCTIONS
    ==================================================
    */


    async function loadProductVariants(
        productId
    ) {

        if (!productId) {

            setVariants([]);

            return [];

        }

        const records =
            await ProductVariantService
                .listByProduct(productId);

        setVariants(records);

        return records;

    }


    async function createVariant(data) {

        if (!user) {

            throw new Error(
                "User is not authenticated."
            );

        }

        if (!brandId) {

            throw new Error(
                "No active brand found."
            );

        }

        const record =
            await ProductVariantService.create({

                ...data,

                brand: brandId,

                owner: user.id

            });


        setVariants(prev => [

            ...prev,

            record

        ]);


        return record;

    }


    async function updateVariant(
        variantId,
        data
    ) {

        const updated =
            await ProductVariantService.update(

                variantId,

                data

            );


        setVariants(prev =>

            prev.map(item =>

                item.id === variantId

                    ? updated

                    : item

            )

        );


        return updated;

    }


    async function deleteVariant(
        variantId
    ) {

        await ProductVariantService.remove(

            variantId

        );


        setVariants(prev =>

            prev.filter(

                item =>
                    item.id !== variantId

            )

        );

    }


    /*
    ==================================================
    REAL-TIME VARIANT SYNCHRONIZATION
    ==================================================
    */

    useEffect(() => {

        if (!selectedProductId) {

            setVariants([]);

            return;

        }

        let active = true;


        const load = async () => {

            try {

                const records =
                    await ProductVariantService
                        .listByProduct(
                            selectedProductId
                        );


                if (active) {

                    setVariants(records);

                }

            }

            catch (error) {

                console.error(
                    "Failed to load product variants:",
                    error
                );

            }

        };


        load();


        const unsubscribe =
            ProductVariantService.subscribe(

                selectedProductId,

                event => {

                    if (!active) {
                        return;
                    }


                    const record =
                        event?.record;


                    if (!record) {
                        return;
                    }


                    /*
                    CREATE
                    */

                    if (
                        event.action === "create"
                    ) {

                        setVariants(prev => {

                            if (
                                prev.some(
                                    item =>
                                        item.id === record.id
                                )
                            ) {

                                return prev;

                            }

                            return [

                                ...prev,

                                record

                            ];

                        });

                    }


                    /*
                    UPDATE
                    */

                    if (
                        event.action === "update"
                    ) {

                        setVariants(prev =>

                            prev.map(item =>

                                item.id === record.id

                                    ? record

                                    : item

                            )

                        );

                    }


                    /*
                    DELETE
                    */

                    if (
                        event.action === "delete"
                    ) {

                        setVariants(prev =>

                            prev.filter(

                                item =>
                                    item.id !== record.id

                            )

                        );

                    }

                }

            );


        return () => {

            active = false;


            if (
                typeof unsubscribe === "function"
            ) {

                unsubscribe();

            }

        };

    }, [selectedProductId]);


    /*
    ==================================================
    PRODUCT SHIPPING
    ==================================================
    */


    async function loadProductShipping(
        productId
    ) {

        if (!productId) {

            setShipping(null);

            return null;

        }

        const record =
            await ProductShippingService
                .getByProduct(productId);


        setShipping(record);

        return record;

    }


    async function saveProductShipping(

        productId,

        data

    ) {

        if (!user) {

            throw new Error(
                "User is not authenticated."
            );

        }

        if (!brandId) {

            throw new Error(
                "No active brand found."
            );

        }

        const record =
            await ProductShippingService
                .saveForProduct(

                    productId,

                    brandId,

                    user.id,

                    data

                );


        setShipping(record);

        return record;

    }


    /*
    ==================================================
    REAL-TIME SHIPPING SYNCHRONIZATION
    ==================================================
    */

    useEffect(() => {

        if (!selectedProductId) {

            setShipping(null);

            return;

        }

        let active = true;


        const load = async () => {

            try {

                const record =
                    await ProductShippingService
                        .getByProduct(
                            selectedProductId
                        );


                if (active) {

                    setShipping(record);

                }

            }

            catch (error) {

                console.error(
                    "Failed to load product shipping:",
                    error
                );

            }

        };


        load();


        const unsubscribe =
            ProductShippingService.subscribe(

                selectedProductId,

                event => {

                    if (!active) {
                        return;
                    }


                    /*
                    CREATE
                    */

                    if (
                        event.action === "create"
                    ) {

                        setShipping(
                            event.record
                        );

                    }


                    /*
                    UPDATE
                    */

                    if (
                        event.action === "update"
                    ) {

                        setShipping(
                            event.record
                        );

                    }


                    /*
                    DELETE
                    */

                    if (
                        event.action === "delete"
                    ) {

                        setShipping(null);

                    }

                }

            );


        return () => {

            active = false;


            if (
                typeof unsubscribe === "function"
            ) {

                unsubscribe();

            }

        };

    }, [selectedProductId]);


    /*
    ==================================================
    PROVIDER
    ==================================================
    */

    return (

        <BrandProductContext.Provider

            value={{

                /*
                Products
                */

                products,

                loading,

                error,

                refreshProducts,

                addProduct,

                editProduct,

                removeProduct,


                /*
                Selected Product
                */

                selectedProductId,

                setSelectedProductId,


                /*
                Variants
                */

                variants,

                loadProductVariants,

                createVariant,

                updateVariant,

                deleteVariant,


                /*
                Shipping
                */

                shipping,

                loadProductShipping,

                saveProductShipping

            }}

        >

            {children}

        </BrandProductContext.Provider>

    );

}


export function useBrandProducts() {

    return useContext(

        BrandProductContext

    );

}